import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbAccordionDirective } from '@ng-bootstrap/ng-bootstrap';
import { FilterIncompatibilityEnum } from 'app/shared/enums/filter-incompatibility.enum.model';
import { RolesEnum } from 'app/shared/enums/roles.enum';
import { PointOfSaleDto } from 'app/shared/models/point-of-sale.model';
import { UtilityFunctions } from 'app/shared/utilities/functions.models';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ButtonTypeEnum } from '../../../../shared/enums/button-type.enum';
import { ColorTypeEnum } from '../../../../shared/enums/color-type.enum';
import {
  FilterGroup,
  FilterItem,
  FilterTask,
  FilterTaskResponse,
  InternalFilterGroup,
} from '../../../../shared/models/filter-group.models';
import { SidebarActionButtons } from '../../../../shared/models/sidebar-action-buttons.model';
import { FilterTypeEnum } from '../../enum/filter-type.enum.model';
import { SearchSettings } from '../../models/common/search-settings.model';
import { FilterMapper } from '../../utilities/filter-mapper';
import { SearchBarService } from './shared/service/search-bar.service';
import { SessionStore } from 'app/shared/state/session.store';
import { ShopContextStore } from 'app/shared/state/shop-context.store';

@Component({
    selector: 'app-search-bar',
    templateUrl: './search-bar.component.html',
    styleUrls: ['./search-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class SearchBarComponent implements OnChanges, AfterViewInit, OnDestroy, OnInit {

  @ViewChild('filterAccordion') public filterAccordion: NgbAccordionDirective;

  @Input() searchBarType: 'search' | 'filter' = 'search';
  @Input() searchText: string;
  @Input() filters: FilterGroup[];
  @Input() settings: SearchSettings = { placeholder: 'Search...', inputId: UtilityFunctions.newGuid(), actionList: [] };
  @Input() clearSerch: Observable<boolean>;
  @Input() customActionsTemplate: TemplateRef<any>;
  @Input() onResetFilter: Subject<void> = new Subject<void>();
  @Input() canUploadFiles: boolean;

  @Output() search = new EventEmitter<string>();
  @Output() action = new EventEmitter<number | string>();
  @Output() applyFilters = new EventEmitter<FilterGroup[]>();
  @Output() currentFiltersEmit = new EventEmitter<InternalFilterGroup[]>();
  @Output() uploadFile = new EventEmitter<void>();

  public currentFilters: InternalFilterGroup[];
  public filterTypeEnum = FilterTypeEnum;
  public internalSearch: string;
  public cacheInternalSearch: string = '';
  public showFilters: boolean;
  public sidebarActionButtons: SidebarActionButtons[] = [
    { text: 'TABLE.FILTERS.APPLY_FILTER', icon: 'check-circle', type: ButtonTypeEnum.default, color: ColorTypeEnum.primary }
  ];
  public showFiltersChips: boolean;
  private eventsSubscription: Subscription;

  private _session = inject(SessionStore);
  private _shopContext = inject(ShopContextStore);

  constructor(
    private router: Router,
    private activeRoute: ActivatedRoute,
    private searchBarService: SearchBarService
  ) { }

  ngOnInit(): void {
    this.eventsSubscription = this.clearSerch?.pipe(filter(x => x)).subscribe(() => {
      this.internalSearch = '';
      this.onSearch();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.settings) {
      if (!!this.settings && !this.settings.inputId) {
        this.settings.inputId = UtilityFunctions.newGuid();
      }
      else if (!this.settings) {
        this.settings = { placeholder: 'Search...', inputId: UtilityFunctions.newGuid() };
      }
    }
    if (changes.searchText) {
      this.internalSearch = this.searchText;
    }
    // Ricostruisce currentFilters quando il parent passa un nuovo riferimento (es. order-grid in Gestione ordini)
    if (changes.filters) {
      const prev = changes.filters.previousValue;
      const curr = changes.filters.currentValue;
      if (curr !== prev) {
        this.defineCurrentFilters();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.settings.enableSearchRoute || this.settings.enableFilterRoute) {
      setTimeout(() => this.checkInitRoute());
    }
    if (this.searchBarType === 'filter') {
      setTimeout(() => this.onToggleFilters());
    }
    this.onResetFilter?.subscribe(() => {
      this.currentFilters.forEach((filter) => filter.selected = []);
      this.onApplyFilters();
    });
  }

  ngOnDestroy() {
    this.eventsSubscription?.unsubscribe();
    // if (this.settings.enableSearchRoute)
    //   this.router.navigate(['.'], { relativeTo: this.activeRoute, skipLocationChange: true, queryParamsHandling: 'merge', queryParams: { search: null } });
  }

  checkInitRoute() {
    const urlQueryParam = this.activeRoute.snapshot.queryParams;
    const queryParamName: string[] = this.filters?.map(x => x.queryParamName);
    const queryFilterBo = queryParamName?.filter(x => urlQueryParam[x]) ?? [];
    if (urlQueryParam.search && urlQueryParam.search !== '') {
      this.cacheInternalSearch = urlQueryParam.search ?? '';
      this.internalSearch = urlQueryParam.search ?? '';
      this.search.emit(urlQueryParam.search);
    }

    let filterTask: FilterTask[] = [];
    if (queryFilterBo?.length > 0) {
      queryFilterBo.forEach((paramName) => {
        const filter = this.filters.find(x => x.queryParamName === paramName);
        if (filter) {
          switch (filter.type) {
            case FilterTypeEnum.agencyFilter:
            case FilterTypeEnum.customerFilter:
            case FilterTypeEnum.ordersFilter:
            case FilterTypeEnum.posFilter:
            case FilterTypeEnum.assortmentFilter:
            case FilterTypeEnum.styleGroupFilter:
            case FilterTypeEnum.productFilter:
            case FilterTypeEnum.userFilter:
            case FilterTypeEnum.seasonReferenceFilter:
            case FilterTypeEnum.customerGroupCodeFilter:
            case FilterTypeEnum.customerSubgroupCodeFilter:
            case FilterTypeEnum.customerHoldingFilter:
            case FilterTypeEnum.customerSignBoardFilter:
            case FilterTypeEnum.customerGroupFilter:
            case FilterTypeEnum.salesTaxGroupFilter:
            case FilterTypeEnum.voucherFilter:
            case FilterTypeEnum.showcasesFilter:
              const taskIds = urlQueryParam[paramName]?.split(',').map(x => +x);
              filterTask.push(new FilterTask({
                ids: taskIds,
                type: filter.type,
                filterGroupId: filter.id
              }));
              if (taskIds?.length > 0) {
                filter.selected = [];
                taskIds.forEach((id) => {
                  filter.selected.push(new FilterItem({ value: id, text: '' }));
                });
              }
              break;
            case FilterTypeEnum.channelFilter:
              filter.selected = [];
              const channelIds = urlQueryParam[paramName]?.split(',').map(x => +x);
              if (channelIds?.length > 0) {
                channelIds.forEach((id) => {
                  filter.selected.push(new FilterItem({
                    text: this._shopContext.getChannelById(id)?.channel.name,
                    value: id,
                  }));
                });
              }
              if (channelIds.length === 1) this.searchBarService.setCurrentChannelFilter(channelIds[0]);
              break;
            case FilterTypeEnum.onlineStoreFilter:
              filter.selected = [];
              const storeIds = urlQueryParam[paramName]?.split(',').map(x => +x);
              if (storeIds?.length > 0) {
                storeIds.forEach((id) => {
                  filter.selected.push(new FilterItem({
                    text: this._shopContext.getShopById(id)?.name,
                    value: id,
                  }));
                });
              }
              break;
            case FilterTypeEnum.radio:
            case FilterTypeEnum.checkbox:
              filter.selected = [];
              filter.selected.push(new FilterItem({
                text: filter.data.find(x => x.value === this.returnValueAfertCheckType(urlQueryParam[paramName]))?.text,
                value: this.returnValueAfertCheckType(urlQueryParam[paramName]),
              }));
              break;
            case FilterTypeEnum.basicCollectionFilter:
              const basicIds = urlQueryParam[paramName]?.split(',').map(x => x);
              filter.selected = [];
              basicIds.forEach((id) => {
                filter.selected.push(new FilterItem({
                  text: id,
                  value: id,
                  checked: true
                }));
              });
              break;
            case FilterTypeEnum.inputFilter:
            case FilterTypeEnum.date:
            case FilterTypeEnum.priceFilter:
            case FilterTypeEnum.amountFilter:
              if (filter.data.length > 1) {
                const inputValues = urlQueryParam[paramName]?.split('|');
                filter.data.forEach((item, index) => {
                  item.value = inputValues[index];
                });
              } else {
                filter.data[0].value = urlQueryParam[paramName];
              }
              break;

            default:
              console.error('Filter type not found');
              break;
          }
        }

      });
      if (filterTask?.length > 0) {
        this.searchBarService.getFilterRootSelected(filterTask).subscribe((res: FilterTaskResponse[]) => {
          res.forEach((element) => {
            const filter = this.filters.find(x => x.id === element.filterGroupId);
            if (filter) {
              filter.selected = [];
              element.ids.forEach((id) => {
                filter.selected.push(new FilterItem({
                  text: this.getTextFilterSelected(element.type, id, element.response),
                  value: id,
                }));
              });
            }
          });
          this.setShowFiltersChips();
          this.defineCurrentFilters();
        });
      } else {
        this.setShowFiltersChips();
        this.defineCurrentFilters();
      }


      this.emitFilters(true);
    }
  }

  // &external-id=ciaocomestai &status=1 &pos=18677 &customer=9034 &agency=1

  getTextFilterSelected(type: FilterTypeEnum, id: number, response: any[]) {
    let result: string = '';
    switch (type) {
      case FilterTypeEnum.posFilter:
        const pos: PointOfSaleDto = response.find(x => x.id === id);
        result = `${pos?.name} - ${pos?.location.address1}`;
        break;

      default:
        const item = response.find(x => x.id === id);
        result = `${item?.name ?? item?.title}`;
        break;
    }
    return result;
  }

  returnValueAfertCheckType(value: any) {
    if (value === 'true') {
      return true;
    } else if (value === 'false') {
      return false;
    } else if (isNaN(Number(value))) {
      return value;
    } else {
      return Number(value);
    }
  }


  public onSearch() {
    this.searchText = this.internalSearch?.trim().toLowerCase();
    if (this.settings.enableSearchRoute) {
      const params: Params = { search: this.searchText || null };
      this.router.navigate(['.'], { relativeTo: this.activeRoute, replaceUrl: true, queryParamsHandling: 'merge', queryParams: params });
    }
    this.search.emit(this.searchText);
  }


  public onAction(action: number | string ) {
    this.action.emit(action);
  }

  public onClearSearch() {
    this.internalSearch = '';
    if (!!this.searchText) this.onSearch();
  }

  public onBlurSearch() {
    if (!this.internalSearch && this.searchText) {
      this.onSearch();
    }
  }

  public onToggleFilters() {
    if (!this.showFilters) {
      this.defineCurrentFilters();
    }
    this.filterCompatibility();
    this.currentFiltersEmit.emit(this.currentFilters);
    if (this.searchBarType === 'search') this.showFilters = !this.showFilters;
  }

  public onUploadFile() {
    this.uploadFile.emit();
  }

  checkRoleFilter(group: InternalFilterGroup): boolean {
    return !!group.role && !group.role.some(x => this._session.userInfo().roles.includes(RolesEnum[x]));
  }

  public onApplyFilters() {
    this.filters = UtilityFunctions.DeepClone(this.currentFilters);
    if (this.settings.enableFilterRoute) this.setFilterQueryParam(this.filters);
    this.emitFilters();
    this.onToggleFilters();
  }

  public onChangeRadio(group: InternalFilterGroup, item: FilterItem) {
    group.selected = [item];
  }

  public onRemoveFilter(group: InternalFilterGroup, chip?: FilterItem) {
    switch (group.type) {
      case FilterTypeEnum.radio:
        group.selected = [group.data.find(x => !x.value)];
        break;
      case FilterTypeEnum.agencyFilter:
      case FilterTypeEnum.tierFilter:
      case FilterTypeEnum.cityFilter:
      case FilterTypeEnum.onlineStoreFilter:
      case FilterTypeEnum.customerFilter:
      case FilterTypeEnum.ordersFilter:
      case FilterTypeEnum.roleFilter:
      case FilterTypeEnum.posFilter:
      case FilterTypeEnum.userFilter:
      case FilterTypeEnum.seasonReferenceFilter:
      case FilterTypeEnum.byCustomizationRequest:
      case FilterTypeEnum.voucherFilter:
      case FilterTypeEnum.customerGroupCodeFilter:
      case FilterTypeEnum.customerSubgroupCodeFilter:
      case FilterTypeEnum.customerSignBoardFilter:
      case FilterTypeEnum.customerHoldingFilter:
      case FilterTypeEnum.customerGroupFilter:
      case FilterTypeEnum.salesTaxGroupFilter:
      case FilterTypeEnum.showcasesFilter:
        group.selected = [new FilterItem({ text: 'STATUS.ALL', value: null })];
        break;
      case FilterTypeEnum.channelFilter:
        group.selected = [new FilterItem({ text: 'STATUS.ALL', value: null })];
        this.searchBarService.setCurrentChannelFilter(null);
        break;
      case FilterTypeEnum.checkbox:
      case FilterTypeEnum.assortmentFilter:
      case FilterTypeEnum.styleGroupFilter:
      case FilterTypeEnum.variantSize:
      case FilterTypeEnum.paletteFilter:
      case FilterTypeEnum.basicCollectionFilter:
      case FilterTypeEnum.productFilter:
        group.selected = group.selected.filter(x => x !== chip);
        break;
      case FilterTypeEnum.date:
      case FilterTypeEnum.priceFilter:
      case FilterTypeEnum.amountFilter:
      case FilterTypeEnum.inputFilter:
        if (group.isRange) {
          group.data.forEach(x => x.value = null);
        } else {
          chip.value = null;
        }
        break;
    }
    this.filters = UtilityFunctions.DeepClone(this.currentFilters);
    this.emitFilters();
    if (this.settings.enableFilterRoute) this.onRemoveFilterOnUrl(group, chip);
  }

  onRemoveFilterOnUrl(group: InternalFilterGroup, chip: FilterItem) {
    let urlQueryParam = UtilityFunctions.DeepClone(this.activeRoute.snapshot.queryParams);
    if (group.selected.length > 1) {
      const param = urlQueryParam[group.queryParamName];
      const newParamValues = param.split(',').filter(x => x !== chip.value.toString());
      urlQueryParam[group.queryParamName] = newParamValues.join(',');
    } else if (group.selected.length === 1) {
      urlQueryParam[group.queryParamName] = null;
    } else if (group.data?.some(x => !!x.value)) {
      if (group.type === FilterTypeEnum.inputFilter) {
        urlQueryParam[group.queryParamName] = group.data.map(x => String(x.value).trim().replace('|', '')).join('|');
      } else {
        urlQueryParam[group.queryParamName] = group.data.map(x => x.value).join('|');;
      }
    }
    this.updateRouteQueryParam(urlQueryParam);
  }

  updateRouteQueryParam(queryParam: Params) {
    this.router.navigate(['.'], {
      queryParams: queryParam,
      relativeTo: this.activeRoute,
      replaceUrl: true,
      queryParamsHandling: 'merge'
    });
  }

  private defineCurrentFilters() {
    this.currentFilters = this.filters ? UtilityFunctions.DeepClone(this.filters.map(x => {
      const f = new InternalFilterGroup(x);
      f.opened = false;
      f.description = this.setFilterDescription(f);
      f.isDisable = !f.isDisable ? this.checkRoleFilter(f) : f.isDisable;
      f.panelId = `panel-${UtilityFunctions.newGuid()}`;
      return f;
    })) : [];
  }

  private emitFilters(disableShowFilterChip?: boolean) {
    this.applyFilters.emit(this.filters);
    if (!disableShowFilterChip) this.setShowFiltersChips();
  }

  private setShowFiltersChips() {
    this.showFiltersChips = this.filters.some(x => {
      let res = false;
      switch (x.type) {
        case FilterTypeEnum.radio:
        case FilterTypeEnum.agencyFilter:
        case FilterTypeEnum.tierFilter:
        case FilterTypeEnum.cityFilter:
        case FilterTypeEnum.onlineStoreFilter:
        case FilterTypeEnum.customerFilter:
        case FilterTypeEnum.ordersFilter:
        case FilterTypeEnum.posFilter:
        case FilterTypeEnum.roleFilter:
        case FilterTypeEnum.channelFilter:
        case FilterTypeEnum.userFilter:
        case FilterTypeEnum.seasonReferenceFilter:
        case FilterTypeEnum.byCustomizationRequest:
        case FilterTypeEnum.customerGroupCodeFilter:
        case FilterTypeEnum.customerSubgroupCodeFilter:
        case FilterTypeEnum.customerSignBoardFilter:
        case FilterTypeEnum.customerHoldingFilter:
        case FilterTypeEnum.customerGroupFilter:
        case FilterTypeEnum.salesTaxGroupFilter:
        case FilterTypeEnum.voucherFilter:
        case FilterTypeEnum.showcasesFilter:
          res = !!x.selected && !!x.selected[0] && !!x.selected[0].value?.toString();
          break;
        case FilterTypeEnum.checkbox:
        case FilterTypeEnum.assortmentFilter:
        case FilterTypeEnum.styleGroupFilter:
        case FilterTypeEnum.variantSize:
        case FilterTypeEnum.paletteFilter:
        case FilterTypeEnum.basicCollectionFilter:
        case FilterTypeEnum.productFilter:
          res = !!x.selected && !!x.selected.length;
          break;
        case FilterTypeEnum.date:
        case FilterTypeEnum.priceFilter:
        case FilterTypeEnum.amountFilter:
        case FilterTypeEnum.inputFilter:
          res = !!x.data && !!x.data.filter(x => !!x.value).length;
          break;
      }
      return res;
    });
  }

  setFilterDescription(group: InternalFilterGroup): string {
    let result = ' - not set - ';
    switch (group.type) {
      case FilterTypeEnum.radio:
      case FilterTypeEnum.agencyFilter:
      case FilterTypeEnum.tierFilter:
      case FilterTypeEnum.cityFilter:
      case FilterTypeEnum.onlineStoreFilter:
      case FilterTypeEnum.customerFilter:
      case FilterTypeEnum.ordersFilter:
      case FilterTypeEnum.roleFilter:
      case FilterTypeEnum.posFilter:
      case FilterTypeEnum.userFilter:
      case FilterTypeEnum.channelFilter:
      case FilterTypeEnum.seasonReferenceFilter:
      case FilterTypeEnum.customerGroupCodeFilter:
      case FilterTypeEnum.customerSubgroupCodeFilter:
      case FilterTypeEnum.customerSignBoardFilter:
      case FilterTypeEnum.customerHoldingFilter:
      case FilterTypeEnum.customerGroupFilter:
      case FilterTypeEnum.salesTaxGroupFilter:
      case FilterTypeEnum.voucherFilter:
      case FilterTypeEnum.voucherFilter:
      case FilterTypeEnum.showcasesFilter:
      case FilterTypeEnum.byCustomizationRequest:
        if (group.selected?.length) { result = group.selected[0]?.text; }
        break;
      case FilterTypeEnum.checkbox:
      case FilterTypeEnum.assortmentFilter:
      case FilterTypeEnum.styleGroupFilter:
      case FilterTypeEnum.variantSize:
      case FilterTypeEnum.paletteFilter:
      case FilterTypeEnum.basicCollectionFilter:
      case FilterTypeEnum.productFilter:
        if (group.selected?.length) { result = group.selected.length.toString() + ' setted'; }
        break;
      case FilterTypeEnum.inputFilter:
        if (group.data?.some(x => x.value?.trim().length > 0)) { result = group.data.length === 1 ? 'setted' : group.data.filter(x => x.value?.trim().length > 0).length + ' setted'; }
        break;
      case FilterTypeEnum.date:
        if (group.data?.some(x => !!x.value)) { result = 'Dates setted'; }
        break;
      case FilterTypeEnum.priceFilter:
        if (group.data?.some(x => !!x.value)) { result = 'Price setted'; }
        break;
      case FilterTypeEnum.amountFilter:
        if (group.data?.some(x => !!x.value)) { result = 'Q.ty setted'; }
        break;
    }
    return result;
  }


  setFilterQueryParam(filters: FilterGroup[]) {
    let queryParam: Params = {};
    filters.forEach(filter => {
      const valueSelected = [];
      switch (filter.type) {
        case FilterTypeEnum.date:
        case FilterTypeEnum.priceFilter:
        case FilterTypeEnum.amountFilter:
          if (filter.data?.some(x => !!x.value)) {
            queryParam[filter.queryParamName] = filter.data.map(x => x.value).join('|');
          }
          break;
        case FilterTypeEnum.inputFilter:
          if (filter.data?.some(x => !!x.value)) {
            queryParam[filter.queryParamName] = filter.data.map(x => String(x.value).trim().replace('|', '')).join('|');
          }
          break;
        default:
          if (filter.selected?.length) {
            filter.selected.forEach(x => {
              if (x.value?.toString().trim()) {
                valueSelected.push(x.value);
              }
            });
            if (valueSelected.length) queryParam[filter.queryParamName] = valueSelected.join(',');
          }
          break;
      }
    });
    this.updateRouteQueryParam(queryParam);
  }

  filterCompatibility() {
    if (this.currentFilters.some(x => x.filterIncompatibility?.length)) {
      const listFilterIncompatibility: FilterIncompatibilityEnum[] = FilterMapper.FilterIncompatibility(this.currentFilters);
      const listOfChannelIncompatibility = [FilterIncompatibilityEnum.ChannelNotActivePreOrder];

      if (listFilterIncompatibility.some(x => listOfChannelIncompatibility.includes(x))) {
        setTimeout(() => {
          this.eventsSubscription = this.searchBarService.currentChannelFilter$.subscribe((channelFilter) => {
            this.currentFilters?.forEach(filter => {
              if (filter.filterIncompatibility && filter.filterIncompatibility.includes(FilterIncompatibilityEnum.ChannelNotActivePreOrder)) {
                if (channelFilter && !channelFilter.channel.supportsPreOrders) {
                  filter.opened && filter.panelId ? this.filterAccordion.toggle(filter.panelId) : null;
                  filter.isDisablePanel = true;
                  filter.selected = filter.data.find(x => !x.value) ? [filter.data.find(x => !x.value)] : [];
                } else {
                  filter.isDisablePanel = false;
                }
              }
            });
          });
        });
      }
    }
  }

}
